import { useRef, forwardRef, useImperativeHandle } from 'react';
import { Image, Camera, X } from 'lucide-react';
import AttachmentMenu from '../../../../components/AttachmentMenu';
import AttachMenuCalendarShareButtons from './AttachMenuCalendarShareButtons';

/**
 * ChatAttachmentMenu - Attachment menu for chat messages
 * 
 * Wraps the base AttachmentMenu with chat-specific buttons:
 * - Picture (file upload)
 * - Camera (capture)
 * - Clear (clear chat draft)
 * - Calendar share/request (depends on chat participants)
 */
const ChatAttachmentMenu = forwardRef(({ onClose, onFileSelect, onAddRequestCalendar, onAddShareCalendar, onClear, chatId }, ref) => {
  const attachmentMenuRef = useRef(null);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    triggerFileInput: () => attachmentMenuRef.current?.triggerFileInput(),
    triggerCamera: () => attachmentMenuRef.current?.triggerCamera()
  }));

  const handlePictureClick = () => {
    attachmentMenuRef.current?.triggerFileInput();
  };

  const handleCameraClick = () => {
    attachmentMenuRef.current?.triggerCamera();
  };

  const handleClearClick = () => {
    onClear?.();
    onClose();
  };

  const handleRequestCalendarClick = () => {
    onAddRequestCalendar?.();
    onClose();
  };

  const handleShareCalendarClick = () => {
    onAddShareCalendar?.();
    onClose();
  };

  const buttons = [
    {
      icon: <Image className="h-8 w-8" />,
      label: 'Picture',
      onClick: handlePictureClick,
    },
    {
      icon: <Camera className="h-8 w-8" />,
      label: 'Camera',
      onClick: handleCameraClick,
    },
    {
      icon: <X className="h-8 w-8" />,
      label: 'Clear',
      onClick: handleClearClick,
    },
  ];

  return (
    <AttachmentMenu
      ref={attachmentMenuRef}
      buttons={buttons}
      onClose={onClose}
      onFileSelect={onFileSelect}
      acceptMultiple={true}
    >
      {/* Calendar buttons - determined by chat participants and user role */}
      <AttachMenuCalendarShareButtons
        chatId={chatId}
        onRequestClick={handleRequestCalendarClick}
        onShareClick={handleShareCalendarClick}
      />
    </AttachmentMenu>
  );
});

ChatAttachmentMenu.displayName = 'ChatAttachmentMenu';

export default ChatAttachmentMenu;
